/*
  Mental Wellness Hub - Tab Animation Implementation
  
  This app implements smooth tab transitions using Tailwind CSS animations.
  
  CURRENT IMPLEMENTATION:
  - Uses simple fade-in animation (animate-fade-in) for smooth tab transitions
  - Pure opacity transition: 0 → 1 over 0.4s with ease-out timing
  - Lightweight and performant - no complex mounting/unmounting logic
  - Applied to each tab content wrapper when tab becomes active
  
  ANIMATION DETAILS:
  - Animation Class: animate-fade-in
  - Duration: 0.4s (400ms)
  - Timing Function: ease-out
  - Effect: Pure opacity fade-in (no transform)
  - Keyframes: fadeInPure (0% opacity:0 → 100% opacity:1)
  
  ALTERNATIVE ANIMATIONS AVAILABLE:
  
  1. Current (Simple Fade):
     className="animate-fade-in"           // Pure fade in (0.4s)
  
  2. Other Tailwind Animations:
     className="animate-fade-in-scale"     // Fade + scale
     className="animate-slide-in"          // Slide from right
     className="animate-zoom-in"           // Zoom effect
     className="animate-tab-enter"         // Complex transition with blur
  
  3. Advanced Component (Available):
     <TabContent> - Handles mounting/unmounting with complex transitions
  
  To change animation style:
  - Replace 'animate-fade-in' with any other animation class
  - Or uncomment the advanced TabContent implementation below
  - Or add custom transition classes for manual control
*/
import React, { useState, useEffect } from 'react';
import PomodoroTimer from './PomodoroTimer'
import MoodTracker from './MoodTracker'
import Analytics from './Analytics'
import MoodChart from './MoodChart'
import DataViewer from './DataViewer'
import MoodCalendar from './MoodCalendar'
import TabContent from './TabTransition'
import './App.css'

function App() {
  const [activeTab, setActiveTab] = useState('pomodoro')
  const [darkMode, setDarkMode] = useState(false)

  // Load dark mode preference from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode')
    if (savedDarkMode) {
      setDarkMode(JSON.parse(savedDarkMode))
    }
  }, [])

  // Apply dark mode to document class and save preference
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode))
  }, [darkMode])

  const tabs = [
    { id: 'pomodoro', label: '🍅 Focus Timer', component: PomodoroTimer },
    { id: 'mood', label: '😊 Mood Tracker', component: MoodTracker },
    { id: 'calendar', label: '📅 Mood Calendar', component: MoodCalendar },
    { id: 'analytics', label: '📊 Analytics', component: Analytics },
    { id: 'chart', label: '📈 Mood Chart', component: MoodChart },
    { id: 'data', label: '📋 Data Viewer', component: DataViewer }
  ]

  const toggleDarkMode = () => {
    setDarkMode(!darkMode)
  }

  const handleTabChange = (tabId) => {
    if (tabId !== activeTab) {
      setActiveTab(tabId)
    }
  }

  return (
    <div className="min-h-screen font-sans antialiased transition-colors duration-500 app-gradient">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-700/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Title */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">MW</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                Mental Wellness Hub
              </h1>
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
              aria-label="Toggle dark mode"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="sticky top-16 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-4">
            <div className="flex space-x-1 bg-gray-100/80 dark:bg-gray-800/80 rounded-2xl p-1 backdrop-blur-sm">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    nav-pill transition-all duration-300 ease-out transform
                    ${activeTab === tab.id
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25 scale-105'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-700/50'
                    }
                  `}
                  aria-pressed={activeTab === tab.id}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative min-h-[600px]">
            {/* Simple fade-in animation for smooth tab transitions */}
            {tabs.map((tab) => {
              const Component = tab.component
              return activeTab === tab.id ? (
                <div
                  key={tab.id}
                  className="animate-fade-in"
                >
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-soft-lg border border-white/20 dark:border-gray-700/30 p-6 sm:p-8 lg:p-10">
                    <Component darkMode={darkMode} />
                  </div>
                </div>
              ) : null
            })}

            {/* Option 2: Advanced Tab Transitions (available if needed) */}
            {/* 
            {tabs.map((tab) => {
              const Component = tab.component
              return (
                <TabContent
                  key={tab.id}
                  activeTab={activeTab}
                  tabId={tab.id}
                  className="absolute inset-0"
                >
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-soft-lg border border-white/20 dark:border-gray-700/30 p-6 sm:p-8 lg:p-10">
                    <Component darkMode={darkMode} />
                  </div>
                </TabContent>
              )
            })}
            */}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-t border-gray-200/50 dark:border-gray-700/50 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Take care of your mental health, one session at a time. 💜
            </p>
            <div className="mt-4 flex justify-center space-x-6">
              <span className="text-xs text-gray-400 dark:text-gray-500">
                Focus • Breathe • Reflect • Grow
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App
